import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import axios from 'axios';
import CosmicBlueprint from '../components/CosmicBlueprint';
import FeedbackBox from "./FeedbackBox";

const SIGNS = [
  'Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo',
  'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces'
];

const TOPICS = [
  'Love & Relationships',
  'Career & Success',
  'Money & Finance',
  'Wellbeing & Health',
  'Spiritual Growth',
  'Family & Home',
];

const PRODUCT_META = {
  core: {
    label: "Celestial Core",
    desc: "Personalized daily/weekly horoscopes delivered to your email or phone",
    price: "£4.99/mo",
    stripePriceId: "price_1RdIOGPRYnhTasCNmqTQI9Sp",
  },
  guardian: {
    label: "Transit Guardian",
    desc: "Real-time SMS/email alerts for key cosmic moments in your chart",
    price: "£9.99/mo",
    stripePriceId: "price_1RdITTPRYnhTasCN18LiX2nw",
  },
  blueprint: {
    label: "Cosmic Blueprint",
    desc: "One-off, in-depth PDF natal report with love, career, and finance advice",
    price: "£29 one-off",
    stripePriceId: "price_1RdIVDPRYnhTasCNyug9gAit",
  },
};

const Dashboard = () => {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();

  // Query string for Stripe success/cancel
  const query = new URLSearchParams(location.search);
  const success = query.get("success");
  const canceled = query.get("canceled");

  // Data state
  const [weekly, setWeekly] = useState({});
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [showBirthForm, setShowBirthForm] = useState(false);

  // Editable user profile details
  const [birthDetails, setBirthDetails] = useState({
    fullName: "",
    birthDate: "",
    birthTime: "",
    birthPlace: "",
    topics: [],
  });
  const [savedBirthDetails, setSavedBirthDetails] = useState(false);

  // Stripe product ownership (from backend)
  const [products, setProducts] = useState([]);
  // Personalized readings
  const [personalDaily, setPersonalDaily] = useState("");
  const [loadingPersonal, setLoadingPersonal] = useState(false);
  const [personalErr, setPersonalErr] = useState("");
  const [personalWeeklyPreview, setPersonalWeeklyPreview] = useState("");
  // Payment notification
  const [notification, setNotification] = useState("");

  // --- Utility to refetch user profile (for product status, etc) ---
  const refetchUser = async () => {
    if (!user) return;
    const token = localStorage.getItem("token");
    const res = await axios.get("/api/user/me", {
      headers: { Authorization: `Bearer ${token}` }
    });
    const u = res.data;
    setBirthDetails({
      fullName: u.fullName || "",
      birthDate: u.birthDate || "",
      birthTime: u.birthTime || "",
      birthPlace: u.birthPlace || "",
      topics: u.topics || [],
    });
    setSavedBirthDetails(!!(u.fullName && u.birthDate && u.birthPlace));
    setProducts(u.products || []);
  };

  // On mount: fetch user profile and weeklies
  useEffect(() => {
    refetchUser();
    setLoading(true);
    axios.get('/api/horoscope/weekly')
      .then((res) => {
        setWeekly(res.data);
        setLoading(false);
      })
      .catch(e => {
        setErr("Could not fetch horoscopes. Please try again later.");
        setLoading(false);
      });
    // eslint-disable-next-line
  }, [user, navigate]);

  // Show payment result messages, and clear the URL after showing. Refetch user for product changes.
  useEffect(() => {
    if (success) {
      setNotification("🎉 Payment successful! Your upgrade will be activated soon.");
      refetchUser();
      window.history.replaceState({}, document.title, "/dashboard");
    } else if (canceled) {
      setNotification("❌ Payment cancelled. No changes made.");
      window.history.replaceState({}, document.title, "/dashboard");
    }
    // eslint-disable-next-line
  }, [success, canceled]);

  // Fetch personalized daily/weekly when details/products saved or changed
  useEffect(() => {
    if (
      savedBirthDetails &&
      birthDetails.fullName &&
      birthDetails.birthDate &&
      birthDetails.birthPlace
    ) {
      setLoadingPersonal(true);
      setPersonalErr("");
      axios.post('/api/horoscope/personal-daily', {
        ...birthDetails,
        topics: birthDetails.topics || [],
        products: products || [],
      }).then(res => {
        setPersonalDaily(res.data.horoscope);
        setLoadingPersonal(false);
      }).catch(() => {
        setPersonalDaily("");
        setPersonalErr("Could not load your unique daily reading.");
        setLoadingPersonal(false);
      });

      axios.post('/api/horoscope/personal-weekly', {
        ...birthDetails,
        topics: birthDetails.topics || [],
        products: products || [],
      }).then(res => {
        const firstPara = res.data.horoscope.split('\n').filter(Boolean)[0];
        setPersonalWeeklyPreview(firstPara);
      }).catch(() => setPersonalWeeklyPreview(""));
    }
    // eslint-disable-next-line
  }, [savedBirthDetails, birthDetails, JSON.stringify(products)]);

  // Save or edit profile
  const handleBirthInfoSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        "/api/user/birth-details",
        birthDetails,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSavedBirthDetails(true);
      setShowBirthForm(false);
      refetchUser();
    } catch (err) {
      alert("Failed to save details.");
    }
  };

  // Checkbox handler for topics
  const handleTopicChange = (topic) => {
    setBirthDetails((prev) => ({
      ...prev,
      topics: prev.topics.includes(topic)
        ? prev.topics.filter((t) => t !== topic)
        : [...prev.topics, topic]
    }));
  };

  // Stripe checkout for upgrades
  const handleUpgrade = async (priceId, productKey) => {
    if (products.includes(productKey) && productKey !== "blueprint") return;
    try {
      const token = localStorage.getItem("token");
      await axios.post("/api/payments/create-checkout-session", {
        priceId,
        customerEmail: user.email,
      }, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => {
          window.location.href = res.data.url; // Stripe checkout redirect
        });
    } catch {
      alert("Payment initiation failed. Please try again.");
    }
  };

  // --- UI render ---
  if (!user) return null;

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-indigo-900 to-purple-950 text-white px-4 py-8">
      <div className="max-w-3xl mx-auto bg-white bg-opacity-10 p-6 rounded-2xl shadow-xl">
        <h1 className="text-2xl font-bold mb-4">Welcome back, {user.fullName || user.email} 🌟</h1>
        {notification && (
          <div className="mb-4 text-center text-green-400 bg-green-900 bg-opacity-40 px-4 py-2 rounded-xl">
            {notification}
          </div>
        )}

        {/* Editable Profile Panel */}
        <section className="mb-8">
          <h2 className="text-lg font-semibold mb-2">Your Birth & Interest Details</h2>
          <div className="bg-black bg-opacity-30 rounded-xl p-4 mb-3">
            {showBirthForm ? (
              <form onSubmit={handleBirthInfoSubmit} className="space-y-2">
                <input
                  type="text"
                  placeholder="Full Name"
                  className="bg-black bg-opacity-30 text-white px-2 py-1 rounded w-full"
                  value={birthDetails.fullName}
                  onChange={e => setBirthDetails({ ...birthDetails, fullName: e.target.value })}
                  required
                  disabled={loadingPersonal}
                />
                <input
                  type="date"
                  placeholder="Birth Date"
                  className="bg-black bg-opacity-30 text-white px-2 py-1 rounded w-full"
                  value={birthDetails.birthDate}
                  onChange={e => setBirthDetails({ ...birthDetails, birthDate: e.target.value })}
                  required
                  disabled={loadingPersonal}
                />
                <input
                  type="time"
                  placeholder="Birth Time"
                  className="bg-black bg-opacity-30 text-white px-2 py-1 rounded w-full"
                  value={birthDetails.birthTime}
                  onChange={e => setBirthDetails({ ...birthDetails, birthTime: e.target.value })}
                  required
                  disabled={loadingPersonal}
                />
                <input
                  type="text"
                  placeholder="Place of Birth (city, country)"
                  className="bg-black bg-opacity-30 text-white px-2 py-1 rounded w-full"
                  value={birthDetails.birthPlace}
                  onChange={e => setBirthDetails({ ...birthDetails, birthPlace: e.target.value })}
                  required
                  disabled={loadingPersonal}
                />
                <div>
                  <div className="font-semibold text-purple-200 mb-1">Topics of Interest:</div>
                  <div className="flex flex-wrap gap-2">
                    {TOPICS.map(topic => (
                      <label key={topic} className="flex items-center space-x-1">
                        <input
                          type="checkbox"
                          className="form-checkbox accent-purple-500"
                          checked={birthDetails.topics.includes(topic)}
                          onChange={() => handleTopicChange(topic)}
                          disabled={loadingPersonal}
                        />
                        <span>{topic}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div className="flex gap-2 mt-2">
                  <button type="submit" className="bg-purple-700 hover:bg-purple-800 text-white font-semibold py-2 px-4 rounded-xl w-full" disabled={loadingPersonal}>
                    Save My Details
                  </button>
                  <button type="button" onClick={() => setShowBirthForm(false)} className="w-full text-sm text-gray-400 underline">
                    Cancel
                  </button>
                </div>
              </form>
            ) : (
              <>
                <div className="mb-1">
                  <span className="font-semibold text-purple-200">Name:</span> {birthDetails.fullName}<br />
                  <span className="font-semibold text-purple-200">Birth Date:</span> {birthDetails.birthDate}<br />
                  <span className="font-semibold text-purple-200">Birth Time:</span> {birthDetails.birthTime || 'Unknown'}<br />
                  <span className="font-semibold text-purple-200">Birth Place:</span> {birthDetails.birthPlace}
                </div>
                <div>
                  <span className="font-semibold text-purple-200">Topics of Interest:</span> {birthDetails.topics.length ? birthDetails.topics.join(', ') : 'None'}
                </div>
                <button className="mt-2 bg-purple-600 hover:bg-purple-700 text-white py-1 px-4 rounded-xl" onClick={() => setShowBirthForm(true)}>
                  Edit Details
                </button>
              </>
            )}
          </div>
        </section>

        {/* 1. Free: Week-ahead for all signs */}
        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-4">Your Week Ahead: All Signs</h2>
          {loading ? (
            <p>Loading...</p>
          ) : err ? (
            <p className="text-red-400">{err}</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {SIGNS.map(sign => (
                <div key={sign} className="bg-gradient-to-br from-indigo-800 to-purple-800 bg-opacity-60 rounded-xl p-4 shadow hover:scale-105 transition-transform">
                  <h3 className="text-lg font-bold mb-2 text-purple-200">{sign}</h3>
                  <p className="text-indigo-100 whitespace-pre-line text-sm">{weekly[sign]}</p>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* 2. Persuasive trust-building CTA (shown if missing) */}
        {!savedBirthDetails && !showBirthForm && (
          <section className="upgrade-panel mb-8">
            <h2 className="text-lg font-semibold mb-2">✨ Discover Your True Horoscope</h2>
            <div className="text-green-200 text-xs mb-1">
              <span className="font-bold">How it works:</span> Your personal reading is generated with *your actual birth chart and the current planetary positions*—just like a real astrologer!
            </div>
            <p>
              <b>Enter your exact birth date, time, and place</b> to receive a <b>free sample</b> of your truly personal daily horoscope—calculated by advanced algorithms using ephemeris data and real astrological rules (not just generic AI).
            </p>
            <button
              onClick={() => setShowBirthForm(true)}
              className="mt-3 bg-purple-600 hover:bg-purple-700 text-white font-bold py-2 px-4 rounded-xl"
            >
              Personalize My Reading
            </button>
          </section>
        )}

        {/* 3. Show personal daily & weekly preview if details are present */}
        {savedBirthDetails && (
          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-2">🔒 Your Personalized Daily Horoscope</h2>
            <div className="mb-2 text-sm text-green-300 font-semibold">
              {`This reading is generated *uniquely* for you, ${birthDetails.fullName}, born on ${birthDetails.birthDate} at ${birthDetails.birthTime || "unknown time"} in ${birthDetails.birthPlace}—using your birth chart and today's planetary positions.`}
            </div>
            <div className="bg-indigo-950 rounded-xl p-4 shadow">
              <span className="text-indigo-300 text-xs inline-flex items-center">
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 17v.01M8 12h8M7 12a5 5 0 0110 0v5a2 2 0 01-2 2H9a2 2 0 01-2-2v-5z" /></svg>
                Only you can view this
              </span>
              {loadingPersonal ? (
                <p className="text-indigo-200 mt-2">Generating your personal reading…</p>
              ) : personalErr ? (
                <p className="text-red-400 mt-2">{personalErr}</p>
              ) : (
                <p className="text-lg text-purple-100 whitespace-pre-line mt-2">{personalDaily}</p>
              )}

              {!loadingPersonal && !personalErr && personalDaily && (
                  <FeedbackBox type="personal-daily" />
              )}

            </div>
            <h3 className="text-md font-bold mb-1 mt-4">Preview: Your Personalized Week Ahead</h3>
            <div className="bg-gradient-to-br from-purple-900 to-indigo-800 rounded-xl p-4 mb-3">
              {personalWeeklyPreview ? (
                <p className="text-indigo-200 whitespace-pre-line">
                  {personalWeeklyPreview}
                  <span className="font-semibold text-purple-300">… <i>Unlock the full week with Celestial Core!</i></span>
                </p>
              ) : (
                <p className="text-gray-400">Loading preview…</p>
              )}

              {personalWeeklyPreview && (
                  <FeedbackBox type="personal-weekly" />
              )}

            </div>
          </section>
        )}

        {/* 4. Premium upsell panel */}
        <section className="mb-8">
          <h2 className="text-lg font-semibold mb-2">Go Further With MysticMegabyte ✨</h2>
          <ul className="space-y-1 mb-2">
            {Object.entries(PRODUCT_META).map(([key, meta]) => (
              <li key={key}>
                <span className="inline-flex items-center">
                  {(products.includes(key) && key !== "blueprint") && <span className="mr-1 text-green-300">☑️</span>}
                  <b>{meta.label}</b>: {meta.desc}
                </span>
              </li>
            ))}
          </ul>
          <div className="flex flex-col gap-4 mt-3">
            {['core', 'guardian'].map((key) => {
              const meta = PRODUCT_META[key];
              return (
                <button
                  key={key}
                  className={`font-bold py-2 px-4 rounded-xl flex items-center justify-center transition
                    ${key === "core" && "bg-yellow-400 hover:bg-yellow-500 text-black"}
                    ${key === "guardian" && "bg-blue-300 hover:bg-blue-400 text-black"}
                    ${(products.includes(key) && key !== "blueprint") ? "opacity-60 cursor-not-allowed" : ""}
                  `}
                  disabled={products.includes(key) && key !== "blueprint"}
                  onClick={() => handleUpgrade(meta.stripePriceId, key)}
                >
                  {products.includes(key) && key !== "blueprint"
                    ? `✓ ${meta.label} – Active`
                    : `${meta.label} – ${meta.price}`}
                </button>
              );
            })}

            <CosmicBlueprint
              user={user}
              products={products}
              blueprintPriceId={PRODUCT_META.blueprint.stripePriceId}
              onBlueprintPurchased={() => {
                refetchUser();
              }}
            />
          </div>
        </section>
        <button
          onClick={() => navigate("/admin")}
          className="w-full bg-purple-700 hover:bg-purple-800 text-white py-2 rounded-xl font-semibold mb-3"
          >
          Admin Panel
        </button>

        <button onClick={logout} className="w-full bg-red-700 hover:bg-red-800 text-white py-2 rounded-xl font-semibold">
          Log Out
        </button>
      </div>
    </div>
  );
};

export default Dashboard;
